import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Col, Layout, Row } from 'antd';
import parse from 'html-react-parser';

import AppFaq from '../components/faq/Faq';
import Loading from '../components/loading/Loading';
import AppFooter from '../components/footer/Footer';
import GlobalError from '../components/globalError/GlobalError';
import ContentError from '../components/reward-types/error/ContentError';
import Cancellation from '../components/reward-types/cancel/Cancellation';
import RewardConfirmation from '../components/reward-types/confirmation/Confirmation';
import Description from '../components/reward-types/description/Description';
import { Logger } from '../utils/Logger';
import { RewardForm } from '../components/RewardForm/RewardForm';
import { ColoredBlock } from '../components/ColoredBlock/ColoredBlock';
import { StyledLayout } from '../components/layout/Layout.style';
import { StyledFooter } from '../components/footer/Footer.style';
import Image, { StyledHeader } from '../components/header/Header.style';
import { RewardExperiencePayloadDto, RewardType } from '@luckycart/xpn-types';
import {
  FetchRewardExperienceResponse,
  rewardExperienceServiceInstance,
} from '../services/reward.experience.service';

import ShieldTick from './images/shield-tick.svg';
import safeExternalLink from '../utils/SafeExternalLink';

const { Content } = Layout;
const PAYEMENT_URI_CERTS = 'https://seal.godaddy.com/verifySeal?sealID=mfOvzZQHDd1Rx59l0O4qnqpr0HsvJYB4Y043DiqjS30f6OafzWKn9S1Fy5kQ';

export interface ProvidedQueryParams {
  siteKey : string;
  shopperId : string;
  experienceId : string;
  operationId: string;
}

export const RewardExperienceController: React.FunctionComponent = () => {
  const [searchParams] = useSearchParams();
  const [rewardExperience, setRewardExperience] = useState<FetchRewardExperienceResponse | null>(null);
  const [isFilled, setIsFilled] = useState<boolean>(false);
  const [isAvailable, setIsAvailable] = useState<boolean>(true);
  const [rewardType, setRewardType] = useState<RewardType|null>(null);
  const [queryParams, setQueryParams] = useState<ProvidedQueryParams>({ siteKey: '', shopperId: '', experienceId: '', operationId: '' });
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    const payload = rewardExperience?.experience?.payload as RewardExperiencePayloadDto;
    setIsFilled(payload?.isFilled ?? false);
    if (payload?.type !== null && payload?.type !== undefined) {
      setRewardType(payload.type);
    }
    if (payload?.isAvailable !== null && payload?.isAvailable !== undefined) {
      setIsAvailable(payload.isAvailable);
    }
  }, [rewardExperience]);
  useEffect(() => {
    const siteKey = searchParams.get('siteKey');
    const shopperId = searchParams.get('shopperId');
    const experienceId = searchParams.get('experienceId');
    const operationId = searchParams.get('operationId');
    if (siteKey === null || shopperId === null || experienceId === null || operationId === null) {
      Logger.log('Missing data to display the experience');
      setIsLoading(false);
      return;
    }
    setQueryParams({ siteKey, shopperId, experienceId, operationId });
    rewardExperienceServiceInstance.findRewardExperienceWithOperationAndConfiguration(shopperId, siteKey, experienceId, operationId)
      .then((rewardExperienceResponse: FetchRewardExperienceResponse) => setRewardExperience(rewardExperienceResponse))
      .finally(() => setIsLoading(false));
  }, [searchParams]);

  if (isLoading) {
    return <Loading/>;
  }

  if (rewardExperience === null || rewardExperience === undefined) {
    return <GlobalError/>;
  }

  return (
    <>
      <Row>
        <Col xs={{ span: 24 }} sm={{ span: 20, offset: 2 }} md={{ span: 18, offset: 3 }} xl={{ span: 12, offset: 6 }} xxl={{ span: 12, offset: 6 }}>
          <StyledLayout>
            <Row>
              <Col>
                <StyledHeader><Image src={rewardExperience?.rewardConfiguration.payment.images.preheader.file} /></StyledHeader>
              </Col>
            </Row>
            <Content>
              {
                (() => {
                  if (rewardExperience.experience !== undefined) {
                    return <>
                      <Row>
                        <>
                          {(() => {
                            if (!isAvailable) {
                              return <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }} lg={{ span: 18, offset: 3 }} xl={{ span: 18, offset: 3 }}>
                                <Cancellation title={rewardExperience.rewardConfiguration.errors.canceled.title}
                                  subtitle={rewardExperience.rewardConfiguration.errors.canceled.subtitle}
                                  desc={parse(rewardExperience.rewardConfiguration.errors.canceled.desc)}
                                />
                              </Col>;
                            }
                            if (isFilled) {
                              return <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }} lg={{ span: 18, offset: 3 }} xl={{ span: 18, offset: 3 }}>
                                <RewardConfirmation title={rewardExperience.rewardConfiguration.payment.secure.title}
                                  subtitle={rewardExperience.rewardConfiguration.payment.secure.subtitle}
                                  desc={parse(rewardExperience.rewardConfiguration.payment.secure.desc ?? '')}
                                />
                              </Col>;
                            }
                            return <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 14, offset: 5 }}>
                              <Description
                                isActive={rewardExperience.rewardConfiguration.payment.securitydesc.activ !== 'none'}
                                html={rewardExperience.rewardConfiguration.payment.securitydesc.html}
                              />
                              <RewardForm
                                config={rewardExperience.rewardConfiguration.payment}
                                queryParams={queryParams}
                                setIsFilled={setIsFilled}
                                rewardType={rewardType}
                              />
                            </Col>;
                          })()
                          }
                        </>
                      </Row>
                      <Row>
                        <Col>
                          <ColoredBlock
                            icon={ShieldTick}
                            themeColor="safe"
                            text={parse(rewardExperience.rewardConfiguration.payment.texts.securityText)}
                            buttonText={safeExternalLink({ href: PAYEMENT_URI_CERTS, children: "Plus d'infos" })}
                          />
                        </Col>
                      </Row>
                      {/* <Row>*/}
                      {/*  <Col>*/}
                      {/*    <ColoredBlock*/}
                      {/*      icon={MessageQuestionCircle}*/}
                      {/*      themeColor="info"*/}
                      {/*      text={parse(rewardExperience.rewardConfiguration.payment.texts.securityText)}*/}
                      {/*      buttonText="Contacter"*/}
                      {/*      buttonIcon={Contact}*/}
                      {/*    />*/}
                      {/*  </Col>*/}
                      {/* </Row>*/}
                    </>;
                  }
                  return <ContentError
                    desc={parse(rewardExperience.rewardConfiguration.errors.unknown.desc)}
                    subtitle={rewardExperience.rewardConfiguration.errors.unknown.subtitle}
                    title={rewardExperience.rewardConfiguration.errors.unknown.title}
                  />;
                })()
              }
            </Content>
            <StyledFooter>
              <AppFaq faq={rewardExperience?.rewardConfiguration.payment.faq}/>
              <AppFooter legals={rewardExperience?.rewardConfiguration.payment.legals} />
            </StyledFooter>
          </StyledLayout>
        </Col>
      </Row>
    </>
  );
};
